import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { GroupCallback, GroupSelectionResult } from '@qm/contracts/connection-groups';
import { Button, Heading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff } from '../../../lib/bff';
import { channelName, groupCopy } from './group-copy';
import { metaErrorKind, ProviderErrorReportSchema, type ProviderErrorReport } from '@qm/contracts/meta-oauth-error';
import './connection-groups.css';
export interface GroupCallbackParams {
  state: string | null;
  code: string | null;
  error: string | null;
  errorReason?: string | null;
  errorCode?: string | null;
}

/** Copy key for a failed provider sign-in (plain language; the raw codes are only reported for the audit). */
export function providerErrorCopyKey(p: Pick<GroupCallbackParams, 'error' | 'errorReason' | 'errorCode'>): 'metaDenied' | 'metaPermissions' | 'metaUnavailable' | 'failed' {
  if (!p.error || p.error === 'invalid_callback') return 'failed';
  const kind = metaErrorKind(p);
  return kind === 'denied' ? 'metaDenied' : kind === 'permissions' ? 'metaPermissions' : kind === 'unavailable' ? 'metaUnavailable' : 'failed';
}

/** Body for /v1/connectors/oauth/group/provider-error, or null when there is nothing valid to report. */
export function providerErrorReport(p: GroupCallbackParams): ProviderErrorReport | null {
  if (!p.state || !p.error || p.error === 'invalid_callback') return null;
  const r = ProviderErrorReportSchema.safeParse({
    state: p.state, error: p.error,
    ...(p.errorReason ? { errorReason: p.errorReason } : {}),
    ...(p.errorCode ? { errorCode: p.errorCode } : {}),
  });
  return r.success ? r.data : null;
}
export function GroupConnectCallback({ params }: { params: GroupCallbackParams }) {
  const { locale, me } = useApp(),
    t = groupCopy(locale);
  const [pending, setPending] = useState<GroupCallback | null>(null),
    [chosen, setChosen] = useState<string[]>([]),
    [result, setResult] = useState<GroupSelectionResult | null>(null);
  const [busy, setBusy] = useState(true),
    [error, setError] = useState('');
  const started = useRef(false),
    owner = useRef<string | null>(null);
  const request = <T,>(path: `/v1/${string}`, body: unknown) =>
    bff<T>(path, {
      method: 'POST',
      headers: { 'x-tenant-id': owner.current ?? '' },
      body: JSON.stringify(body),
    });
  useEffect(() => {
    if (started.current || !me?.tenantId) return;
    started.current = true;
    owner.current = me.tenantId;
    // Keep the code out of history, analytics and subsequent reloads.
    history.replaceState(null, '', window.location.pathname);
    if (params.error || !params.state || !params.code) {
      setError(t[providerErrorCopyKey(params)]);
      setBusy(false);
      // record the provider's codes against this attempt (audit only; the screen does not depend on it)
      const report = providerErrorReport(params);
      if (report) void request('/v1/connectors/oauth/group/provider-error', report).catch(() => undefined);
      return;
    }
    void request<GroupCallback>('/v1/connectors/oauth/group/callback', {
      state: params.state,
      code: params.code,
    })
      .then((data) => {
        if (!['/setup', '/integrations'].includes(data.returnTo))
          throw new Error('Invalid return destination');
        setPending(data);
        setBusy(false);
      })
      .catch(() => {
        setError(t.failed);
        setBusy(false);
      });
  }, [me?.tenantId]);
  const back = () =>
    window.location.assign(pending?.returnTo === '/setup' ? '/setup' : '/integrations');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !pending || owner.current !== me?.tenantId) return;
    if (!chosen.length) {
      setError(t.none);
      return;
    }
    setBusy(true);
    setError('');
    try {
      setResult(
        await request<GroupSelectionResult>('/v1/connectors/oauth/group/select', {
          pendingId: pending.pendingId,
          assetIds: chosen,
        }),
      );
    } catch {
      setError(t.failed);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="qm-page qm-group-callback"
      aria-labelledby="page-title"
      data-testid="group-connect-callback"
    >
      <Heading level={1} id="page-title">
        {result ? t.results : t.choose}
      </Heading>
      {busy ? <Text role="status">{t.working}</Text> : null}
      {error ? <Text role="alert">{error}</Text> : null}
      {owner.current && me?.tenantId && owner.current !== me.tenantId ? (
        <Text role="alert">{t.failed}</Text>
      ) : null}
      {result ? (
        <>
          <Text role="status">{result.failed.length ? t.partial : t.success}</Text>
          <ul>
            {result.connected.map((r, i) => (
              <li key={`ok-${i}`}>
                {channelName(r.channel, locale)} —{' '}
                {r.status === 'connected' ? t.connected : t.pending}
              </li>
            ))}
            {result.failed.map((r) => (
              <li key={r.id}>
                {channelName(r.channel, locale)} — {t.failed}
              </li>
            ))}
          </ul>
          <Text>{t.noAutomatic}</Text>
          <Button onClick={back}>{t.next}</Button>
        </>
      ) : pending ? (
        <form onSubmit={(e) => void submit(e)}>
          <Text>{t.chooseHint}</Text>
          <Text tone="muted">{t.readiness}</Text>
          <fieldset disabled={busy || me?.tenantId !== owner.current}>
            <legend className="qm-sr-only">{t.choose}</legend>
            {pending.services.map((service) => (
              <section key={service.channel}>
                <Heading level={2}>{channelName(service.channel, locale)}</Heading>
                {service.status !== 'available' ? (
                  <p className="qm-group-service-message">{t[service.status]}</p>
                ) : null}
                {pending.assets
                  .filter((asset) => asset.channel === service.channel)
                  .map((asset) => {
                    const unavailable =
                      asset.channel === 'google_search_console' && !asset.verified;
                    return (
                      <label className="qm-group-asset" key={asset.id}>
                        <input
                          type="checkbox"
                          value={asset.id}
                          disabled={unavailable}
                          checked={chosen.includes(asset.id)}
                          onChange={(e) =>
                            setChosen((ids) =>
                              e.target.checked
                                ? [...ids, asset.id]
                                : ids.filter((id) => id !== asset.id),
                            )
                          }
                        />
                        <span>
                          <bdi>{asset.name}</bdi>
                          {unavailable ? ` — ${t.notVerified}` : ''}
                        </span>
                      </label>
                    );
                  })}
              </section>
            ))}
            <div className="qm-connection-buttons">
              <Button variant="primary" type="submit" disabled={busy || !chosen.length}>
                {t.save}
              </Button>
              <Button type="button" onClick={back}>
                {t.back}
              </Button>
            </div>
          </fieldset>
        </form>
      ) : !busy ? (
        <Button onClick={back}>{t.back}</Button>
      ) : null}
    </section>
  );
}
